import React, { useState, useEffect } from 'react';
import './EditVendor.css';
import { API_URL } from "./API";

const EditVendor = ({ onBack, onVendorUpdated, vendorData }) => {
  const [formData, setFormData] = useState({
    vendor_name: '',
    name: '',
    contact_number: '',
    email: '',
    gst_no: '',
    pan_no: '',
    state: '',
    address: '',
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    status: 'Active',
    vendor_logo: null
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);

  const STATUS_OPTIONS = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    if (vendorData) {
      let vendor = vendorData;
      
      if (vendorData.data) {
        vendor = vendorData.data;
      } else if (vendorData.originalData && vendorData.originalData.data) {
        vendor = vendorData.originalData.data;
      }
      
      const newFormData = {
        vendor_name: vendor.vendor_name || '',
        name: vendor.name || '',
        contact_number: vendor.contact_number || '',
        email: vendor.email || '',
        gst_no: vendor.gst_no || '',
        pan_no: vendor.pan_no || '',
        state: vendor.state || '',
        address: vendor.address || '',
        bank_name: vendor.bank_name || '',
        bank_account_number: vendor.bank_account_number || '',
        ifsc_code: vendor.ifsc_code || '',
        status: vendor.status || 'Active',
        vendor_logo: vendor.vendor_logo || null
      };
      
      setFormData(newFormData);
      
      if (vendor.vendor_logo) {
        const logoUrl = vendor.vendor_logo.startsWith('http') 
          ? vendor.vendor_logo 
          : `${API_URL}/${vendor.vendor_logo}`;
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }
      
      setLogoChanged(false);
    }
  }, [vendorData]);

  const validateContactNumber = (value) => {
    if (value === '') return '';
    if (!/^\d+$/.test(value)) {
      return 'Contact number must contain only digits';
    }
    if (value.length !== 10) {
      return 'Contact number must be exactly 10 digits';
    }
    return '';
  };

  const validatePanNumber = (value) => {
    if (value === '') return '';
    if (value.length !== 10) {
      return 'PAN number must be exactly 10 characters';
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
      return 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'contact_number') {
      const error = validateContactNumber(value);
      setErrors(prev => ({
        ...prev,
        contact_number: error
      }));
    } else if (name === 'pan_no') {
      const error = validatePanNumber(value.toUpperCase());
      setFormData(prev => ({
        ...prev,
        pan_no: value.toUpperCase()
      }));
      setErrors(prev => ({
        ...prev,
        pan_no: error
      }));
    } else if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, GIF, WEBP)';
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return 'File size should be less than 5MB';
    }
    
    return null;
  };

  const processFile = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors(prev => ({
        ...prev,
        vendor_logo: error
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      
      setFormData(prev => ({
        ...prev,
        vendor_logo: base64String
      }));
      
      setLogoPreview(base64String);
      setLogoChanged(true);
    };
    reader.readAsDataURL(file);

    if (errors.vendor_logo) {
      setErrors(prev => ({
        ...prev,
        vendor_logo: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      vendor_logo: null
    }));
    setLogoPreview(null);
    setLogoChanged(true);
    
    const fileInput = document.getElementById('editVendorLogo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const updateVendor = async (vendorData) => {
    try {
      const response = await fetch(`${API_URL}/vendors/update_vendor`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(vendorData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || `HTTP error! status: ${response.status}` };
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
      
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to server. Please check if the backend is running on ${API_URL}`);
      }
      
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const contactError = validateContactNumber(formData.contact_number);
    const panError = validatePanNumber(formData.pan_no);
    
    if (contactError || panError) {
      setErrors({
        contact_number: contactError,
        pan_no: panError
      });
      
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const updateData = {
        vendor_id: vendorData.vendor_id || vendorData.id,
        vendor_name: formData.vendor_name.trim(),
        name: formData.name.trim(),
        contact_number: formData.contact_number.trim(),
        email: formData.email.trim(),
        gst_no: formData.gst_no.trim(),
        pan_no: formData.pan_no.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        bank_name: formData.bank_name.trim(),
        bank_account_number: formData.bank_account_number.trim(),
        ifsc_code: formData.ifsc_code.trim(),
        status: formData.status
      };

      if (logoChanged) {
        updateData.vendor_logo = formData.vendor_logo;
      }

      const updateResult = await updateVendor(updateData);
      
      const updatedVendor = {
        ...vendorData,
        vendor_name: updateResult.data.vendor_name,
        name: updateResult.data.name,
        contact_number: updateResult.data.contact_number,
        email: updateResult.data.email,
        gst_no: updateResult.data.gst_no,
        pan_no: updateResult.data.pan_no,
        state: updateResult.data.state,
        address: updateResult.data.address,
        bank_name: updateResult.data.bank_name,
        bank_account_number: updateResult.data.bank_account_number,
        ifsc_code: updateResult.data.ifsc_code,
        status: updateResult.data.status,
        vendor_logo: updateResult.data.vendor_logo,
        updated_on: updateResult.data.updated_on,
        originalData: updateResult
      };

      if (onVendorUpdated) {
        onVendorUpdated(updatedVendor);
      }
      
      alert('Vendor updated successfully!');
      
      if (onBack) {
        onBack();
      }

    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to update vendor. Please try again.'
      });
      
      alert('Error: ' + (error.message || 'Failed to update vendor. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="EV-00">
      <div className="EV-01">
        <div className="EV-02">
          <h3 className="EV-03">Edit Vendor</h3>
        </div>
        
        <div className="EV-04">
          <div className="EV-05">
            {errors.submit && (
              <div className="EV-submit-error">
                {errors.submit}
              </div>
            )}

            <div className="EV-row">
              <div className="EV-12">
                <label className="EV-13" htmlFor="vendor_name">
                  Vendor Name
                </label>
                <input
                  type="text"
                  id="vendor_name"
                  name="vendor_name"
                  className="EV-15"
                  value={formData.vendor_name}
                  onChange={handleInputChange}
                  placeholder="Enter Vendor Name"
                  disabled={isLoading}
                />
              </div>

              <div className="EV-18">
                <label className="EV-19" htmlFor="name">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="EV-21"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Name"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EV-row">
              <div className="EV-24">
                <label className="EV-25" htmlFor="contact_number">
                  Contact Number
                </label>
                <input
                  type="text"
                  id="contact_number"
                  name="contact_number"
                  className="EV-27"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter Contact Number"
                  disabled={isLoading}
                />
                {errors.contact_number && (
                  <span className="EV-38">{errors.contact_number}</span>
                )}
              </div>

              <div className="EV-74">
                <label className="EV-75" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="EV-76"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Email"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EV-row">
              <div className="EV-18">
                <label className="EV-19" htmlFor="gst_no">
                  GST No
                </label>
                <input
                  type="text"
                  id="gst_no"
                  name="gst_no"
                  className="EV-21"
                  value={formData.gst_no}
                  onChange={handleInputChange}
                  placeholder="Enter GST No"
                  disabled={isLoading}
                />
              </div>

              <div className="EV-74">
                <label className="EV-75" htmlFor="pan_no">
                  PAN No
                </label>
                <input
                  type="text"
                  id="pan_no"
                  name="pan_no"
                  className="EV-76"
                  value={formData.pan_no}
                  onChange={handleInputChange}
                  placeholder="Enter PAN No"
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.pan_no && (
                  <span className="EV-38">{errors.pan_no}</span>
                )}
              </div>
            </div>

            <div className="EV-row">
              <div className="EV-77">
                <label className="EV-78" htmlFor="state">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  className="EV-79"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter State"
                  disabled={isLoading}
                />
              </div>

              <div className="EV-56">
                <label className="EV-57" htmlFor="bank_name">
                  Bank Name
                </label>
                <input
                  type="text"
                  id="bank_name"
                  name="bank_name"
                  className="EV-59"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Name"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EV-row">
              <div className="EV-62">
                <label className="EV-63" htmlFor="bank_account_number">
                  Bank Account Number
                </label>
                <input
                  type="text"
                  id="bank_account_number"
                  name="bank_account_number"
                  className="EV-65"
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  placeholder="Enter Bank Account Number"
                  disabled={isLoading}
                />
              </div>

              <div className="EV-68">
                <label className="EV-69" htmlFor="ifsc_code">
                  IFSC Code
                </label>
                <input
                  type="text"
                  id="ifsc_code"
                  name="ifsc_code"
                  className="EV-71"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC Code"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="EV-80">
              <label className="EV-81" htmlFor="address">
                Address
              </label>
              <textarea
                id="address"
                name="address"
                className="EV-82"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                disabled={isLoading}
                rows="3"
              />
            </div>

            <div className="EV-12">
              <label className="EV-13" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="EV-15 EV-status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="EV-30">
              <label className="EV-31" htmlFor="editVendorLogo">
                Vendor Logo
              </label>
              
              <div 
                className={`EV-42 ${isDragOver ? 'EV-43' : ''} ${errors.vendor_logo ? 'EV-44' : ''} ${isLoading ? 'EV-disabled' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !isLoading && document.getElementById('editVendorLogo').click()}
              >
                <input
                  type="file"
                  id="editVendorLogo"
                  name="editVendorLogo"
                  className="EV-58"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
                
                {!logoPreview ? (
                  <div className="EV-45">
                    <div className="EV-46">
                      <svg className="EV-47" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                    </div>
                    <div className="EV-48">
                      <span className="EV-49">Click to upload</span> or drag and drop
                    </div>
                    <div className="EV-50">
                      PNG, JPG, GIF up to 5MB
                    </div>
                  </div>
                ) : (
                  <div className="EV-51">
                    <img src={logoPreview} alt="Logo preview" className="EV-52" />
                    <div className="EV-53">
                      <button 
                        type="button" 
                        className="EV-54" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }} 
                        title="Remove image"
                        disabled={isLoading}
                      >
                        <svg className="EV-55" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {errors.vendor_logo && <span className="EV-38">{errors.vendor_logo}</span>}
            </div>
          </div>

          <div className="EV-39">
            <button 
              type="button" 
              className="EV-40" 
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </button>
            <button 
              type="button" 
              className="EV-41" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditVendor;